// Client-модели REST-контракта warehouse (зеркалят бэковые client-DTO из
// services/menuitems/warehouse/v1/models/client и services/menus/warehouse/v1/models/client).
// БД-модели фронт не видит.

export interface PriceDto {
    currencyId: string;
    price: number;
}

export interface WeightDto {
    value: string | null;
    measurement: string | null;
}

export interface CaloriesDto {
    calories: number | null;
    proteins: number | null;
    carbohydrates: number | null;
    fats: number | null;
}

export interface CookingTimeDto {
    fromTime: number | null;
    toTime: number | null;
}

export interface ScheduleDaySlotDto {
    fromHour: number;
    toHour: number;
    fromMinute: number;
    toMinute: number;
}

export interface ScheduleDayDto {
    isEnabled: boolean;
    slots: ScheduleDaySlotDto[];
}

export interface ScheduleDto {
    isEnabled: boolean;
    monday: ScheduleDayDto;
    tuesday: ScheduleDayDto;
    wednesday: ScheduleDayDto;
    thursday: ScheduleDayDto;
    friday: ScheduleDayDto;
    saturday: ScheduleDayDto;
    sunday: ScheduleDayDto;
}

export enum MediaKind {
    image = "image",
    gif = "gif",
    video = "video",
}

export interface MediaItemDto {
    sourceId: string;
    kind: MediaKind;
    source: string;
    // У gif и видео рендиций нет: card и detail пустые, везде показывается source.
    card: string | null;
    detail: string | null;
}

export interface ModifierSetRefDto {
    menuItemModifierSetId: number;
    sortOrder: number;
}

export interface MenuItemDto {
    id: number;
    menuId: number;
    menuCategoryId: number;
    externalId: string | null;
    name: Record<string, string>;
    description: Record<string, string>;
    sellPrice: PriceDto;
    oldPrice: PriceDto | null;
    isInStopList: boolean;
    weight: WeightDto;
    calories: CaloriesDto;
    cookingTimeFromCategory: boolean;
    itemCookingTime: CookingTimeDto;
    badgeIds: number[];
    schedule: ScheduleDto;
    media: MediaItemDto[];
    modifierSets: ModifierSetRefDto[];
    arCodeLink: string | null;
    supplierLogoUrl: string | null;
}

// ===== Экран категорий и позиций =====

// Строка сайдбара категорий: счётчики приходят вместе с категорией, отдельным
// запросом на категорию их было бы 70+.
export interface MenuCategoryDto {
    id: number;
    menuId: number;
    name: Record<string, string>;
    sortOrder: number;
    isInStopList: boolean;
    schedule: ScheduleDto;
    cookingTime: CookingTimeDto;
    itemsCount: number;
    stopListItemsCount: number;
    externalId: string | null;
}

export interface MenuScopeSummaryDto {
    categoriesCount: number;
    itemsCount: number;
    stopListItemsCount: number;
    stopListCategoriesCount: number;
}

export interface MenuCategoriesPageDto {
    categories: MenuCategoryDto[];
    summary: MenuScopeSummaryDto;
}

// Плитка позиции — лёгкая версия MenuItemDto: без media-массива, расписания,
// калорий и бейджей. Полную позицию отдаёт GET /menu-items/{id}.
export interface MenuItemListDto {
    id: number;
    menuId: number;
    menuCategoryId: number;
    name: Record<string, string>;
    description: Record<string, string>;
    sellPrice: PriceDto;
    oldPrice: PriceDto | null;
    isInStopList: boolean;
    previewPhotoUrl: string | null;
    previewPhotoKind: MediaKind;
    modifierSetsCount: number;
    hasExternalId: boolean;
    sortOrder: number;
}

// Цифры на чипах фильтров. Считаются по меню/категории/поиску и не зависят от
// выбранного чипа — иначе выбор одного обнулял бы соседние счётчики.
export interface MenuItemsCountsDto {
    allCount: number;
    stopListCount: number;
    withoutPhotoCount: number;
}

// total и counts приходят только на первой странице (skip = 0) и равны null дальше:
// это свойства выборки, а не страницы. Клиент их уже получил, а на догрузке они стоили
// бы трёх лишних запросов к БД на каждую прокрутку.
export interface MenuItemsPageDto {
    items: MenuItemListDto[];
    total: number | null;
    counts: MenuItemsCountsDto | null;
}

// notInStopList чипом не показывается — это выборка кандидатов для диалога
// «Убрать из продажи» на экране стоп-листа.
export type MenuItemsListFilterDto = "all" | "stopList" | "notInStopList" | "noPhoto";

// Проблемные строки файла импорта: пустой список — весь файл разобран.
export interface MenuImportResultDto {
    errors: string[];
}

// ===== Настройки меню =====

export type MenuTypeDto = "Regular";

// Меню в списке кабинета: обложка с логотипом и два счётчика на карточке. Этот же DTO
// читают списки выбора (копирование позиции, будущий селект меню в шапке) — лишние для них
// поля дешевле второй ручки с почти тем же телом.
// Меню для выпадающего списка — только то, чем его подписать (см. MenuListDto со счётчиками).
export interface MenuOptionDto {
    id: number;
    name: Record<string, string>;
}

export interface MenuListDto {
    id: number;
    type: MenuTypeDto;
    name: Record<string, string>;
    sortOrder: number;
    isMenuEnabled: boolean;
    hasExternalIntegration: boolean;
    photos: MenuPhotosDto;
    categoriesCount: number;
    itemsCount: number;
}

export type ThemeIdDto = "default" | "expensiveBlack";

export type StorefrontPhotosModeDto =
    | "defaultWithPhotos"
    | "defaultNoPhotos"
    | "verticalWithPhotos";

export interface MenuPhotosDto {
    logoPhotoUrl: string | null;
    backgroundPhotoUrl: string | null;
}

export interface ContactDetailsDto {
    phoneNumber: string | null;
    websiteUrl: string | null;
    whatsApp: string | null;
    instagram: string | null;
    telegram: string | null;
}

export interface AddressDetailsDto {
    mapsUrl: string | null;
    title: Record<string, string>;
    region: Record<string, string>;
}

export interface WorkingScheduleSlotDto {
    fromHour: number;
    fromMinute: number;
    toHour: number;
    toMinute: number;
}

export interface WorkingScheduleDayDto {
    isDayOff: boolean;
    slots: WorkingScheduleSlotDto[];
}

export interface WorkingScheduleDto {
    timezoneInMinutes: number;
    monday: WorkingScheduleDayDto;
    tuesday: WorkingScheduleDayDto;
    wednesday: WorkingScheduleDayDto;
    thursday: WorkingScheduleDayDto;
    friday: WorkingScheduleDayDto;
    saturday: WorkingScheduleDayDto;
    sunday: WorkingScheduleDayDto;
}

// Языки меню приходят плоским списком включённых локалей, а не объектом с булями.
export interface AvailableLanguagesDto {
    enabledLocales: string[];
}

export interface ThemeSettingsDto {
    themeId: ThemeIdDto;
    storefrontPhotosMode: StorefrontPhotosModeDto;
    buttonsBackgroundColor: string | null;
    categoriesBackgroundColor: string | null;
    oldPriceLabel: Record<string, string> | null;
}

// ===== Способы заказа =====

export type DeliveryTypeDto =
    | "ToRestaurantTable"
    | "InsideTheRestaurant"
    | "ToAddress"
    | "TakeAway"
    | "TakeAwayToTime"
    | "ToVehicle";

export interface CoordinatesDto {
    latitude: number;
    longitude: number;
}

export interface DeliveryZoneDto {
    name: string;
    freeDeliveryMinimalOrderPrice: PriceDto | null;
    fixedDeliveryPrice: PriceDto | null;
    coordinates: CoordinatesDto[];
}

export interface DeliveryConditionsFixedPriceDto {
    type: "FixedPrice";
    fixedDeliveryPrice: PriceDto;
    freeDeliveryMinimalOrderPrice: PriceDto;
}

export interface DeliveryConditionsZonePriceDto {
    type: "ZonePrice";
    deliveryZones: DeliveryZoneDto[];
}

export type DeliveryConditionsDto =
    | DeliveryConditionsFixedPriceDto
    | DeliveryConditionsZonePriceDto;

export interface DeliverySettingsToRestaurantTableDto {
    type: "ToRestaurantTable";
    isEnabled: boolean;
    description: Record<string, string>;
    canChooseSeat: boolean;
    isChangeTableEnabled: boolean;
}

export interface DeliverySettingsInsideTheRestaurantDto {
    type: "InsideTheRestaurant";
    isEnabled: boolean;
    description: Record<string, string>;
}

export interface DeliverySettingsToAddressDto {
    type: "ToAddress";
    isEnabled: boolean;
    description: Record<string, string>;
    deliveryConditions: DeliveryConditionsDto;
}

export interface DeliverySettingsTakeAwayDto {
    type: "TakeAway";
    isEnabled: boolean;
    description: Record<string, string>;
}

export interface DeliverySettingsTakeAwayToTimeDto {
    type: "TakeAwayToTime";
    isEnabled: boolean;
    description: Record<string, string>;
    isDateRequired: boolean;
}

export interface DeliverySettingsToVehicleDto {
    type: "ToVehicle";
    isEnabled: boolean;
    description: Record<string, string>;
}

export type DeliverySettingsDto =
    | DeliverySettingsToRestaurantTableDto
    | DeliverySettingsInsideTheRestaurantDto
    | DeliverySettingsToAddressDto
    | DeliverySettingsTakeAwayDto
    | DeliverySettingsTakeAwayToTimeDto
    | DeliverySettingsToVehicleDto;

// ===== Способы оплаты =====

export type PaymentTypeDto = "Offline" | "CloudPaymentsCheckout" | "SBPTochka";

export interface AmountDto {
    currencyId: string;
    amount: number;
}

export interface ServiceFeeDto {
    isEnabled: boolean;
    title: Record<string, string>;
    amount: AmountDto;
}

export interface PaymentSettingsDto {
    type: PaymentTypeDto;
    isEnabled: boolean;
    serviceFee: ServiceFeeDto;
    externalId: string | null;
}

// ===== Чаты =====

export type MenuChatMessengerDto = "Telegram" | "Max";

export type StaffCallKindDto = "Waiter" | "Bill" | "Hookah" | "Administrator";

export interface ReportingFilterWhitelistedOrderStatusesDto {
    type: "WhitelistedOrderStatuses";
    orderStatuses: string[];
}

export interface ReportingFilterCallWaiterForTableDto {
    type: "CallWaiterForTable";
}

export interface ReportingFilterBlacklistedTablesPublicIdsDto {
    type: "BlacklistedTablesPublicIds";
    tablesPublicIds: number[];
}

export interface ReportingFilterWhitelistedDeliveryTypesDto {
    type: "WhitelistedDeliveryTypes";
    deliveryTypes: string[];
}

export type ReportingFilterDto =
    | ReportingFilterWhitelistedOrderStatusesDto
    | ReportingFilterCallWaiterForTableDto
    | ReportingFilterBlacklistedTablesPublicIdsDto
    | ReportingFilterWhitelistedDeliveryTypesDto;

export interface MenuChatDto {
    messenger: MenuChatMessengerDto;
    chatId: number;
    chatTitle: string | null;
    botId: number;
    filters: ReportingFilterDto[];
}

// Терминалы iiko — только для показа: их привязывает интеграция, не форма настроек.
export interface IikoTerminalDto {
    terminalGroupId: string;
}

// ===== Информационный поп-ап =====

export interface InfoMessageDto {
    isEnabled: boolean;
    title: Record<string, string>;
    description: Record<string, string>;
    callToAction: Record<string, string>;
    mediaUrl: string;
    mediaType: string;
}

export interface MenuSettingsDto {
    id: number;
    type: MenuTypeDto;
    name: Record<string, string>;
    description: Record<string, string>;
    addressDetails: AddressDetailsDto;
    contactDetails: ContactDetailsDto;
    photos: MenuPhotosDto;
    workingSchedule: WorkingScheduleDto;
    availableLanguages: AvailableLanguagesDto;
    themeSettings: ThemeSettingsDto;
    deliverySettings: DeliverySettingsDto[];
    paymentSettings: PaymentSettingsDto[];
    chats: MenuChatDto[];
    iikoTerminals: IikoTerminalDto[];
    infoMessage: InfoMessageDto;
    isMenuEnabled: boolean;
    isCartEnabled: boolean;
    isOrderCreationEnabled: boolean;
    isPromoCodesEnabled: boolean;
    staffCalls: StaffCallKindDto[];
    isLoyaltyProgramEnabled: boolean;
    hasExternalIntegration: boolean;
    // Отдельно от hasExternalIntegration: внешних интеграций две (iiko Cloud и
    // OpenRestifyApi), а привязка позиций к внешним товарам есть только у iiko.
    hasIikoIntegration: boolean;
    // Фича филиала, приезжающая вместе с меню: она решает, доедет ли до гостя подпись к
    // зачёркнутой цене. Включается точечно из sudo.
    isOldPriceLabelEnabled: boolean;
}

// ===== Интеграция меню с iiko =====

// Состояние подключения целиком: каждая мутация отдаёт его же, поэтому перезапрашивать
// интеграцию после действия не нужно.
export interface IikoIntegrationDto {
    isEnabled: boolean;
    apiLogin: string | null;
    iikoOrganizationId: string | null;
    // Jackson отдаёт Date числом (epoch ms) — как и в остальных REST-моделях.
    lastDataSyncAt: number | null;
    isPriceSyncEnabled: boolean;
    catalogSource: IikoCatalogSourceType;
    externalMenuId: string | null;
    priceCategoryId: string | null;
    terminals: IikoTerminalDto[];
}

export interface IikoWebhookDto {
    isConnected: boolean;
    registeredUri: string | null;
    stopLists: boolean;
    tableOrders: boolean;
    deliveryOrders: boolean;
}

// Внешние меню и типы цен iiko отдаёт одним запросом, поэтому и здесь они вместе.
export interface IikoExternalMenusDto {
    menus: IikoReferenceItemDto[];
    priceCategories: IikoReferenceItemDto[];
}

// Откуда берём каталог iiko: номенклатура iiko Office или внешнее меню iikoWeb.
export type IikoCatalogSourceType = "Nomenclature" | "ExternalMenu";

export interface IikoReferenceItemDto {
    id: string;
    name: string;
}

// ===== Каталог внешнего (iiko) меню =====

export interface ExternalProductDto {
    id: string;
    name: Record<string, string>;
    price: PriceDto;
}

export interface ExternalProductsPageDto {
    items: ExternalProductDto[];
    total: number;
}

// ===== Опции внешнего (iiko) товара =====

export interface ExternalSizeOptionDto {
    id: string;
    name: Record<string, string>;
    price: PriceDto;
}

export interface ExternalGroupModifierItemOptionDto {
    id: string;
    name: Record<string, string>;
    price: PriceDto;
    minAmount: number;
    maxAmount: number;
    required: boolean;
}

export interface ExternalGroupModifierOptionDto {
    id: string;
    name: Record<string, string>;
    minAmount: number;
    maxAmount: number;
    required: boolean;
    items: ExternalGroupModifierItemOptionDto[];
}

export interface ExternalModifierOptionDto {
    id: string;
    name: Record<string, string>;
    price: PriceDto;
    minAmount: number;
    maxAmount: number;
    required: boolean;
}

export interface ExternalProductOptionsDto {
    sizes: ExternalSizeOptionDto[];
    groupModifiers: ExternalGroupModifierOptionDto[];
    modifiers: ExternalModifierOptionDto[];
}

/** Совпадает с Config.MAX_REPORTING_BOT_ID на бэкенде */
export const MAX_REPORTING_BOT_ID = 1;

/**
 * Числовой id Telegram-бота отправки уведомлений (@RestifyBot).
 * Должен совпадать с id бота из Config.TELEGRAM_BOT_TOKEN на бэкенде
 * (префикс токена до ":"), иначе bots[botId] не резолвится и сообщения не уходят.
 */
export const TELEGRAM_REPORTING_BOT_ID = 1522497896;
